import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Items with a `to` are real route links (separate pages); the rest scroll to a
// section on the homepage.
const NAV_ITEMS: { id: string; label: string; to?: string }[] = [
  { id: 'classes',       label: 'Classes' },
  { id: 'schedule',      label: 'Schedule' },
  { id: 'coaches',       label: 'Coaches' },
  { id: 'physiotherapy', label: 'Physiotherapy' },
  { id: 'membership',    label: 'Membership' },
  { id: 'events',        label: 'Events' },
  { id: 'about',         label: 'About Us' },
  { id: 'contact',       label: 'Contact' },
];

export default function Navbar() {
  const [isOpen, setIsOpen]           = useState(false);
  const [scrolled, setScrolled]       = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const trigger = window.scrollY + window.innerHeight * 0.35;
      let current = '';
      for (const { id } of NAV_ITEMS) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= trigger) current = id;
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = `/#${id}`;
    }
    setIsOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-black/75 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={() => {
              const el = document.getElementById('hero');
              if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
              } else {
                window.location.href = '/';
              }
            }}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <img
              src="assets/hub30Logo.jpg"
              alt="HUB30 Logo"
              className="h-11 w-11 object-cover rounded-full border-2 border-[#14B8A6] group-hover:border-teal-300 transition-colors"
            />
            <span className="text-white font-bold text-lg tracking-widest hidden sm:block group-hover:text-[#14B8A6] transition-colors">
              HUB30
            </span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {NAV_ITEMS.map(({ id, label, to }) => {
              const isActive = activeSection === id;
              const inner = (
                <>
                  <span
                    className={`text-sm font-medium transition-colors duration-200 ${
                      isActive ? 'text-[#14B8A6]' : 'text-white/80 group-hover:text-white'
                    }`}
                  >
                    {label}
                  </span>
                  {/* Active underline dot */}
                  <span
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full bg-[#14B8A6] transition-all duration-300 ${
                      isActive ? 'w-4 opacity-100' : 'w-0 opacity-0 group-hover:w-2 group-hover:opacity-50'
                    }`}
                  />
                </>
              );
              return to ? (
                <Link key={id} to={to} className="relative px-3 py-2 group cursor-pointer">
                  {inner}
                </Link>
              ) : (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className="relative px-3 py-2 group cursor-pointer"
                >
                  {inner}
                </button>
              );
            })}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden w-11 h-11 flex items-center justify-center text-white hover:text-[#14B8A6] transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-2xl transition-all duration-300 ${isOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        } bg-black/95 backdrop-blur-md border-t border-white/10`}
      >
        <div className="px-4 py-4 space-y-1">
          {NAV_ITEMS.map(({ id, label, to }) => {
            const isActive = activeSection === id;
            const cls = `flex items-center justify-between w-full text-left py-3.5 px-4 font-medium rounded-xl transition-all duration-200 cursor-pointer min-h-[48px] ${
              isActive
                ? 'bg-[#14B8A6]/10 text-[#14B8A6] border border-[#14B8A6]/20'
                : 'text-white/80 hover:bg-white/5 hover:text-white border border-transparent'
            }`;
            return to ? (
              <Link key={id} to={to} onClick={() => setIsOpen(false)} className={cls}>
                {label}
              </Link>
            ) : (
              <button key={id} onClick={() => scrollToSection(id)} className={cls}>
                {label}
                {isActive && <i className="ri-checkbox-blank-circle-fill text-[6px] text-[#14B8A6]" />}
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
